import React, { useState } from "react";

import StarsIcon from "@uui/Stars02";
import ThumbsDownIcon from "@uui/ThumbsDown";
import ThumbsUpIcon from "@uui/ThumbsUp";

import Typography from "@mui/material/Typography";

import {
  ActionButton,
  ProposedAnswer as ProposedAnswerContainer,
  RejectedProposedAnswer,
} from "@app/analytics/components/KeyQuestionAnswer/styles";
import { KeyQuestionAnswerClipStatus } from "@app/common/types";

import AutoAnimateContainer from "@rui/AutoAnimateContainer";
import { Link } from "@rui/Link";
import { Column, Row } from "@rui/Stack";
import { Bold } from "@rui/styles";

import type { KeyQuestionProposedAnswerProps } from "./types";

const EditableProposedAnswer = ({
  answer,
  isUpdating,
  onUpdateStatus,
  status,
}: KeyQuestionProposedAnswerProps) => {
  const [rejectTimeout, setRejectTimeout] = useState<
    NodeJS.Timeout | undefined
  >();

  const updateAnswerStatus = (newStatus: KeyQuestionAnswerClipStatus) => {
    if (!onUpdateStatus) return;
    onUpdateStatus(newStatus);
  };

  const handleAccept = () => updateAnswerStatus("user_accept");

  const rejectAnswer = () => {
    updateAnswerStatus("user_reject");
    setRejectTimeout(undefined);
  };

  const handleReject = () => {
    const timeout = setTimeout(rejectAnswer, 5000);
    setRejectTimeout(timeout);
  };

  const handleUndoReject = () => {
    clearTimeout(rejectTimeout);
    setRejectTimeout(undefined);
  };

  return (
    <>
      <AutoAnimateContainer show={!rejectTimeout}>
        <ProposedAnswerContainer className="editable">
          <Column spacing={0.25}>
            <Row>
              <StarsIcon
                sx={{ color: "shades.secondary.500", fontSize: "14px", m: 0.5 }}
              />
              <Typography className="key-question-answer__text">
                <Bold>Proposed answer:</Bold> {answer}
              </Typography>
            </Row>
            <Row variant="left" paddingLeft={2}>
              <ActionButton
                loading={isUpdating}
                startIcon={<ThumbsUpIcon />}
                onClick={handleAccept}
              >
                Accept
              </ActionButton>
              <ActionButton
                startIcon={<ThumbsDownIcon />}
                onClick={handleReject}
              >
                Reject
              </ActionButton>
            </Row>
          </Column>
        </ProposedAnswerContainer>
      </AutoAnimateContainer>
      <AutoAnimateContainer show={Boolean(rejectTimeout) && !status}>
        <RejectedProposedAnswer>
          <Typography className="key-question-answer__text">
            Thanks for your feedback, this proposed answer has been removed.{" "}
            <Link onClick={handleUndoReject}>Undo</Link>
          </Typography>
        </RejectedProposedAnswer>
      </AutoAnimateContainer>
    </>
  );
};

const ProposedAnswer = (props: KeyQuestionProposedAnswerProps) => {
  const canEdit = Boolean(props.onUpdateStatus);

  if (canEdit) {
    return <EditableProposedAnswer {...props} />;
  }

  return (
    <ProposedAnswerContainer>
      <Column spacing={0.25}>
        <Row>
          <StarsIcon
            sx={{ color: "shades.secondary.500", fontSize: "14px", m: 0.5 }}
          />
          <Typography className="key-question-answer__text">
            <Bold>Proposed answer:</Bold> {props.answer}
          </Typography>
        </Row>
      </Column>
    </ProposedAnswerContainer>
  );
};

export default ProposedAnswer;
